import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { expenseService } from '@/api/services/expense.service'
import { useCurrentUserStore } from '@/stores/currentUser.store'
import type {
  BonusAccess,
  CompanyEvent,
  Expense,
  ExpenseCategory,
  ExpenseCreate,
  KmReimbursement,
  KmReimbursementCreate,
} from '@/types/expense.types'

/**
 * Expense suite store — the signed-in user's expenses and km
 * reimbursements plus the reference data the forms need (categories,
 * events, €/km rate). Leader/admin approval lists arrive in a later
 * stack PR.
 */
export const useExpenseStore = defineStore('expense', () => {
  const categories = ref<ExpenseCategory[]>([])
  const events = ref<CompanyEvent[]>([])
  const kmRate = ref<string>('0')
  const myExpenses = ref<Expense[]>([])
  const myKm = ref<KmReimbursement[]>([])
  const pendingExpenseApprovals = ref<Expense[]>([])
  const pendingKmApprovals = ref<KmReimbursement[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  // null = not yet checked (the sidebar triggers the lazy check when the
  // feature flag is on; job-role based, not access-role based).
  const bonusAccess = ref<BonusAccess | null>(null)

  const activeCategories = computed(() => categories.value.filter((c) => c.active))

  async function fetchReferenceData(): Promise<void> {
    const [cats, evts, rate] = await Promise.all([
      expenseService.getCategories(),
      expenseService.getEvents(),
      expenseService.getKmRate(),
    ])
    categories.value = cats
    events.value = evts
    kmRate.value = rate
  }

  async function fetchMine(): Promise<void> {
    const userId = useCurrentUserStore().authUser?.id
    if (!userId) return
    const params = {
      auth_user_id: userId,
      page_size: 100,
      sort_by: 'created_at',
      sort_order: 'desc' as const,
    }
    const [expenses, km] = await Promise.all([
      expenseService.getExpenses(params),
      expenseService.getKmList(params),
    ])
    myExpenses.value = expenses.items
    myKm.value = km.items
  }

  async function createExpense(data: ExpenseCreate, files: File[]): Promise<Expense> {
    loading.value = true
    error.value = null
    try {
      let row = await expenseService.createExpense(data)
      for (const file of files) {
        row = await expenseService.uploadDocument(row.id, file)
      }
      await fetchMine()
      return row
    } catch (e: unknown) {
      error.value = extractErrorKey(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function createKm(data: KmReimbursementCreate): Promise<KmReimbursement> {
    loading.value = true
    error.value = null
    try {
      const row = await expenseService.createKm(data)
      await fetchMine()
      return row
    } catch (e: unknown) {
      error.value = extractErrorKey(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function cancelExpense(id: number): Promise<void> {
    await expenseService.cancelExpense(id)
    await fetchMine()
  }

  async function cancelKm(id: number): Promise<void> {
    await expenseService.cancelKm(id)
    await fetchMine()
  }

  async function uploadDocument(id: number, file: File): Promise<void> {
    await expenseService.uploadDocument(id, file)
    await fetchMine()
  }

  async function fetchPendingApprovals(): Promise<void> {
    const me = useCurrentUserStore().authUser?.id
    // Walk every page (company-wide for admin) — a single-page fetch
    // would silently truncate past 100 pending rows (leave convention).
    async function walk<T extends { auth_user_id: number }>(
      fetcher: (params: Record<string, unknown>) => Promise<{ items: T[]; total: number }>,
    ): Promise<T[]> {
      const items: T[] = []
      let page = 1
      for (;;) {
        const resp = await fetcher({
          status: 'expense.status_pending',
          page,
          page_size: 100,
          sort_by: 'created_at',
          sort_order: 'asc',
        })
        items.push(...resp.items)
        if (items.length >= resp.total || resp.items.length === 0) break
        page += 1
      }
      // Own pending rows are never actionable (no self-approval).
      return items.filter((r) => r.auth_user_id !== me)
    }
    const [expenses, km] = await Promise.all([
      walk((p) => expenseService.getExpenses(p)),
      walk((p) => expenseService.getKmList(p)),
    ])
    pendingExpenseApprovals.value = expenses
    pendingKmApprovals.value = km
  }

  async function decideExpense(id: number, approve: boolean): Promise<void> {
    await expenseService.decideExpense(id, approve)
    pendingExpenseApprovals.value = pendingExpenseApprovals.value.filter((r) => r.id !== id)
  }

  async function checkBonusAccess(): Promise<void> {
    if (bonusAccess.value !== null) return
    try {
      bonusAccess.value = await expenseService.getBonusAccess()
    } catch {
      bonusAccess.value = { own: false, team: false, entry: false }
    }
  }

  async function decideKm(id: number, approve: boolean): Promise<void> {
    await expenseService.decideKm(id, approve)
    pendingKmApprovals.value = pendingKmApprovals.value.filter((r) => r.id !== id)
  }

  return {
    categories,
    activeCategories,
    events,
    kmRate,
    myExpenses,
    myKm,
    pendingExpenseApprovals,
    pendingKmApprovals,
    bonusAccess,
    checkBonusAccess,
    loading,
    error,
    fetchReferenceData,
    fetchMine,
    createExpense,
    createKm,
    cancelExpense,
    cancelKm,
    uploadDocument,
    fetchPendingApprovals,
    decideExpense,
    decideKm,
  }
})

/** Pull the backend detail key (e.g. "expense.cap_exceeded:…") out of an axios error. */
function extractErrorKey(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : 'app.error_generic'
}
